// Opens what WhatsApp's "Export chat" produced: a .zip holding the chat text and its media, a bare
// .txt when media was left out, or a folder where someone already unzipped it.

import { isChatTextName, looksLikeWhatsAppText, NotAWhatsAppExport, titleFromFileName } from "./whatsapp";
import { isZip, readZipDirectory, readZipEntry } from "./zip";

/** A file chosen, dropped or shared, with its path inside a dropped folder when there is one. */
export type InputFile = { file: Blob & { name: string }; path?: string };

export type OpenedExport = {
  /** The name of the file or folder the person chose. */
  name: string;
  /** The chat's name from the file name. */
  title: string;
  text: string;
  /** Names of the media files that came with the chat. */
  media: string[];
  mediaBytes: number;
  /** Reads one media file by the name the chat text uses. */
  readMedia(name: string): Promise<Blob | null>;
};

const MIME: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  mp4: "video/mp4",
  mov: "video/quicktime",
  "3gp": "video/3gpp",
  opus: "audio/ogg",
  ogg: "audio/ogg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  mp3: "audio/mpeg",
  pdf: "application/pdf",
  vcf: "text/vcard",
  txt: "text/plain",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

export function mimeOf(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return MIME[ext] ?? "application/octet-stream";
}

const baseName = (path: string) => path.split(/[\\/]/).pop() ?? path;
const dirName = (path: string) => {
  const parts = path.split(/[\\/]/);
  parts.pop();
  return parts.join("/");
};

async function textOf(blob: Blob): Promise<string> {
  return new TextDecoder("utf-8").decode(await blob.arrayBuffer());
}

/** Opens a zip export. Throws NotAWhatsAppExport when it holds no chat text. */
export async function openZip(file: Blob & { name: string }): Promise<OpenedExport> {
  const entries = await readZipDirectory(file);
  const texts = entries.filter((e) => /\.txt$/i.test(e.name));
  const chat = texts.find((e) => isChatTextName(e.name)) ?? (texts.length === 1 ? texts[0] : undefined);
  if (!chat) throw new NotAWhatsAppExport();
  const text = await textOf(await readZipEntry(file, chat));
  if (!looksLikeWhatsAppText(text)) throw new NotAWhatsAppExport();
  const media = entries.filter((e) => e !== chat);
  const byName = new Map(media.map((e) => [baseName(e.name), e]));
  return {
    name: file.name,
    title: titleFromFileName(file.name === "_chat.txt" ? chat.name : file.name),
    text,
    media: [...byName.keys()],
    mediaBytes: media.reduce((n, e) => n + e.size, 0),
    readMedia: async (name) => {
      const entry = byName.get(baseName(name));
      return entry ? readZipEntry(file, entry, mimeOf(entry.name)) : null;
    },
  };
}

/** Opens a chat text file, with any media files that sat beside it in the same folder. */
export async function openText(
  file: Blob & { name: string },
  siblings: InputFile[] = [],
): Promise<OpenedExport> {
  const text = await textOf(file);
  if (!looksLikeWhatsAppText(text)) throw new NotAWhatsAppExport();
  const byName = new Map(siblings.map((s) => [baseName(s.path ?? s.file.name), s.file]));
  return {
    name: file.name,
    title: titleFromFileName(file.name),
    text,
    media: [...byName.keys()],
    mediaBytes: siblings.reduce((n, s) => n + s.file.size, 0),
    readMedia: async (name) => {
      const found = byName.get(baseName(name));
      return found ? new Blob([found], { type: found.type || mimeOf(name) }) : null;
    },
  };
}

/**
 * Splits what someone chose or dropped into exports. Each zip is one chat. Each chat text file is
 * one chat, and the other files in its folder are its media. A dropped folder named after the chat,
 * such as "WhatsApp Chat - Family", gives that chat its name.
 */
export function groupInputs(
  inputs: InputFile[],
): { kind: "zip" | "text"; main: InputFile; siblings: InputFile[] }[] {
  const out: { kind: "zip" | "text"; main: InputFile; siblings: InputFile[] }[] = [];
  const texts = inputs.filter((i) => /\.txt$/i.test(i.file.name));
  const folderOf = (i: InputFile) => dirName(i.path ?? "");
  for (const input of inputs) {
    if (/\.zip$/i.test(input.file.name)) out.push({ kind: "zip", main: input, siblings: [] });
  }
  for (const text of texts) {
    const folder = folderOf(text);
    const siblings = inputs.filter(
      (i) => i !== text && !/\.(?:zip|txt)$/i.test(i.file.name) && folderOf(i) === folder,
    );
    out.push({ kind: "text", main: text, siblings });
  }
  return out;
}

/** Opens one group from groupInputs. */
export async function openInput(group: ReturnType<typeof groupInputs>[number]): Promise<OpenedExport> {
  const { file, path } = group.main;
  if (group.kind === "zip" || (await isZip(file))) return openZip(file);
  const opened = await openText(file, group.siblings);
  // A bare _chat.txt in a folder takes the folder's name.
  if (baseName(file.name) === "_chat.txt" && path) {
    const folder = baseName(dirName(path));
    if (folder) return { ...opened, title: titleFromFileName(folder) };
  }
  return opened;
}

/**
 * Whether a shared or dropped file looks like a WhatsApp export, by its name and, for a zip, by
 * the chat text inside. Reads at most the zip's directory.
 */
export async function isWhatsAppExport(file: Blob & { name: string }): Promise<boolean> {
  const name = baseName(file.name);
  if (isChatTextName(name)) return true;
  if (/\.zip$/i.test(name) || file.type === "application/zip") {
    if (/^WhatsApp/i.test(name)) return true;
    try {
      const entries = await readZipDirectory(file);
      return entries.some((e) => isChatTextName(e.name));
    } catch {
      return false;
    }
  }
  if (/\.txt$/i.test(name) && file.size < 512 * 1024 * 1024) {
    try {
      return looksLikeWhatsAppText(await textOf(file.slice(0, 4000)));
    } catch {
      return false;
    }
  }
  return false;
}
